import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import {
    carePlans,
    costDrivers,
    extras,
    offerTiers,
    pricingTabs,
} from "@/lib/offers";
import {
    CompanyHeader,
    CompanyTabs,
    offerBtn,
    offerBtnGhost,
    SmartLink,
} from "@/components/CompanyUi";
import { DiagnosticCta, DiagnosticHint } from "@/components/DiagnosticCta";

export default function PricingPage() {
    const [params, setParams] = useSearchParams();
    const tabIds = pricingTabs.map((t) => t.id);
    const tab = tabIds.includes(params.get("tab")) ? params.get("tab") : "offers";
    const current = pricingTabs.find((t) => t.id === tab);

    useEffect(() => {
        document.title = "Pricing — SalesCan";
        return () => {
            document.title = "SalesCan";
        };
    }, []);

    return (
        <main data-testid="pricing-page" className="bg-white text-[#0a0a0b]">
            <CompanyHeader
                kicker="Pricing"
                title="Real offers. Locked numbers."
                subtitle="Starter, Capture, Recovery, Partner — mapped to what we actually sell. No invented package ladder."
            />

            <section className="px-5 pb-20">
                <div className="mx-auto max-w-5xl">
                    <CompanyTabs
                        tabs={pricingTabs}
                        active={tab}
                        onChange={(id) => setParams({ tab: id }, { replace: true })}
                        testId="pricing-tabs"
                    />
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#5b5b66]">{current?.blurb}</p>

                    {tab === "offers" ? <OffersTab /> : null}
                    {tab === "care" ? <CareTab /> : null}
                    {tab === "custom" ? <CustomTab /> : null}
                </div>
            </section>
        </main>
    );
}

function OffersTab() {
    return (
        <div className="mt-8">
            <div className="grid gap-4 sm:grid-cols-2">
                {offerTiers.map((tier) => (
                    <article
                        key={tier.name}
                        className={`flex flex-col rounded-3xl p-6 ring-1 ${
                            tier.highlight
                                ? "bg-[var(--brand)] text-white ring-[var(--brand)]"
                                : "bg-[#f6f7fb] ring-black/5"
                        }`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className={`text-xs font-semibold uppercase tracking-wide ${tier.highlight ? "text-white/70" : "text-[var(--brand)]"}`}>
                                    {tier.name}
                                </p>
                                <h2 className="mt-1 font-display text-2xl font-extrabold">{tier.sku}</h2>
                            </div>
                            <div className="text-right">
                                <p className="font-display text-2xl font-extrabold">{tier.price}</p>
                                <p className={`text-xs ${tier.highlight ? "text-white/70" : "text-[#6b6b75]"}`}>
                                    {tier.cadence}
                                </p>
                            </div>
                        </div>
                        <ul className="mt-5 space-y-2">
                            {tier.points.map((p) => (
                                <li key={p} className="flex gap-2 text-sm leading-relaxed">
                                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${tier.highlight ? "text-white" : "text-[var(--brand)]"}`} />
                                    {p}
                                </li>
                            ))}
                        </ul>
                        <SmartLink
                            href={tier.href}
                            className={`mt-6 ${
                                tier.highlight
                                    ? "rounded-full bg-white px-5 py-2.5 text-center text-sm font-semibold text-[var(--brand)]"
                                    : offerBtn
                            }`}
                        >
                            {tier.cta}
                        </SmartLink>
                    </article>
                ))}
            </div>

            <div className="mt-8 rounded-3xl bg-white p-6 ring-1 ring-black/5">
                <h3 className="font-display text-lg font-bold">Website extras</h3>
                <p className="mt-1 text-sm text-[#5b5b66]">On the $199 site. Not mixed into other SKUs.</p>
                <ul className="mt-4 grid gap-3 sm:grid-cols-3">
                    {extras.map((x) => (
                        <li key={x.name} className="rounded-2xl bg-[#f6f7fb] px-4 py-3">
                            <p className="text-sm font-semibold text-[#0a0a0b]">{x.name}</p>
                            <p className="text-sm text-[var(--brand)]">{x.price}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function CareTab() {
    return (
        <div className="mt-8">
            <div className="grid gap-4 sm:grid-cols-2">
                {carePlans.map((plan) => (
                    <article key={plan.name} className="flex flex-col rounded-3xl bg-[#f6f7fb] p-6 ring-1 ring-black/5">
                        <h2 className="font-display text-xl font-extrabold">{plan.name}</h2>
                        <p className="mt-2 font-display text-2xl font-extrabold text-[var(--brand)]">{plan.price}</p>
                        <ul className="mt-4 flex-1 space-y-2 text-sm leading-relaxed text-[#5b5b66]">
                            {plan.points.map((p) => (
                                <li key={p} className="flex gap-2">
                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand)]" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                        <SmartLink href={plan.href} className={`mt-6 ${offerBtnGhost}`}>
                            See the offer
                        </SmartLink>
                    </article>
                ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-[#5b5b66]">
                Marketing retainers: our fee and your ad budget stay two different numbers. We do not publish a
                retainer package until it is a real SKU. Quoted after the diagnostic.
            </p>
        </div>
    );
}

function CustomTab() {
    return (
        <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-3xl bg-[#f6f7fb] p-6 ring-1 ring-black/5">
                <h2 className="font-display text-2xl font-extrabold">Diagnostic, then a fixed proposal</h2>
                <p className="mt-3 text-[15px] leading-relaxed text-[#5b5b66]">
                    CRM, workflows, custom agents, and anything that is not a live SKU go through the diagnostic.
                    You leave with the leak and a price — whether you hire us or not.
                </p>
                <ul className="mt-5 space-y-2">
                    {costDrivers.map((d) => (
                        <li key={d} className="flex gap-2 text-sm text-[#5b5b66]">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand)]" />
                            {d}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="rounded-3xl bg-white p-6 ring-1 ring-black/5">
                <h3 className="font-display text-lg font-bold">Start here</h3>
                <div className="mt-5">
                    <DiagnosticCta testId="pricing-book-diagnostic" />
                    <DiagnosticHint className="mt-3" />
                </div>
            </div>
        </div>
    );
}
